import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import Button from '../../components/styledComponents/Button';
import { OldSelect } from '../../components/styledComponents/Select/Select';
import { useGetOrg } from '../../services/getOrg';
import { saveOrg } from '../../api';
import { useQueryClient } from '@tanstack/react-query';
import DocumentResultCard from '../../doc-search/DocumentResultCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { notify } from '../../utils/helper';
import Dialog from '../../components/styledComponents/Dialog';
import { useGetSearchResultsByOrg } from '../../services/getSearchResults_V2';

const ArchiveOrgHierarchy = () => {
  const [selectedOrg, setSelectedOrg] = useState<ExtendedOption | undefined>(undefined);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { data: orgData } = useGetOrg();
  const {
    data: searchData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetSearchResultsByOrg(selectedOrg);

  const docs = useMemo(() => searchData?.pages.flatMap((page) => page.hits), [searchData]);
  const count = useMemo(() => searchData?.pages[0].groupCount, [searchData]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 1.0,
    });

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [handleObserver]);

  const handleArchive = async (isArchive: boolean) => {
    const archivedOrgGroupSplit = selectedOrg?.value.split('|');
    if (archivedOrgGroupSplit) {
      const response = await saveOrg([
        {
          type: 'GROUP',
          unit: archivedOrgGroupSplit[0],
          subunit: archivedOrgGroupSplit[1],
          product: archivedOrgGroupSplit[2],
          lineOfBusiness: archivedOrgGroupSplit[3],
          archived: isArchive,
        },
      ]);

      if (response) {
        notify(`Org Hierarchy successfully ${isArchive ? 'archived' : 'reactivated'}`, 'success');
      } else {
        notify(`Failed to ${isArchive ? 'archive' : 'reactivate'} Org Hierarchy`, 'error');
      }

      queryClient.invalidateQueries({ queryKey: ['org'] });
      queryClient.invalidateQueries({ queryKey: ['search-results', selectedOrg?.value] });
      setSelectedOrg(undefined);
    }
  };

  return (
    <Box className="wrapper container">
      <Box className="content-box">
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <Stack gap={3}>
            <h2 className="page-title margin-bottom-0">Archive Org Hierarchy</h2>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <OldSelect
                id="archive"
                label={'What part of the business'}
                value={selectedOrg?.value ?? ''}
                handleChange={(e) => {
                  // this is necessary because the select component accepts a string as the value, not an obejct
                  const selectedOption = orgData?.hierarchyOptionsWithLOB.find(
                    (option) => option.value === e.target.value
                  );

                  if (selectedOption) setSelectedOrg(selectedOption);
                }}
                options={
                  orgData?.hierarchyOptionsWithLOB.sort(
                    (a, b) => Number(a.archived) - Number(b.archived)
                  ) ?? []
                }
                labelSize="small"
                selectSize="small"
                width="200px"
                height="300px"
              />
              <Tooltip
                title={
                  <p style={{ fontSize: '14px', marginBottom: '0px' }}>
                    Archived Org Hierarchies appear at the bottom of the list.
                  </p>
                }
              >
                <InfoOutlinedIcon></InfoOutlinedIcon>
              </Tooltip>
            </Stack>

            {isLoading && (
              <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faSpinner} size="3x" />
              </Stack>
            )}

            {!isLoading && selectedOrg && (
              <Stack className="listing-card-row-results">
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <h3 style={{ marginBottom: '0px' }}>
                    Showing {docs?.length} of {count} Active Forms
                  </h3>
                  <Button
                    sx={{ width: '200px' }}
                    onClick={() => {
                      if (selectedOrg?.archived || docs?.length === 0) {
                        handleArchive(!selectedOrg?.archived);
                      } else {
                        setArchiveDialogOpen(true);
                      }
                    }}
                  >
                    {!selectedOrg?.archived ? 'Archive Org Hierarchy' : 'Reactivate Org Hierarchy'}
                  </Button>
                  <Tooltip
                    PopperProps={{
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '450px',
                        },
                      },
                    }}
                    placement="right-end"
                    title={
                      <p style={{ fontSize: '14px', marginBottom: '0px' }}>
                        To interact with forms, use the search page.
                      </p>
                    }
                  >
                    <InfoOutlinedIcon></InfoOutlinedIcon>
                  </Tooltip>
                </Stack>
                {docs?.map((doc: any, idx: number) => (
                  <DocumentResultCard
                    doc={doc}
                    key={`${idx}-${doc._id}`}
                    isEditor={false}
                    disableCardAnchor={true}
                    isArchive={true}
                  />
                ))}
                {hasNextPage && <div ref={loadMoreRef}>Loading more...</div>}
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
      {archiveDialogOpen && (
        <Dialog
          open={archiveDialogOpen}
          closeDialog={() => setArchiveDialogOpen(false)}
          id={'archive-dialog'}
          cancelText="Cancel"
          maxWidth={480}
          confirmText="Yes, archive"
          confirmClick={() => {
            setArchiveDialogOpen(false);
            handleArchive(!selectedOrg?.archived);
          }}
        >
          <Typography>
            Are you sure you want to archive this hierarchy? Note that this will inactivate all
            active forms and <strong>cannot be undone.</strong>
          </Typography>
        </Dialog>
      )}
    </Box>
  );
};

export default ArchiveOrgHierarchy;
